import { hasStoredTenantSlug, resolveTenantSlug } from './tenant';
import type {
  MessageTemplateDto,
  MessageTemplateInput,
  SupportMessageDto,
  AccessLevel,
  EnrollmentResult,
  LoginOutcome,
  MfaChallengeSent,
  MfaMethodType,
  MfaPendingResponse,
  MfaStatus,
  OtpEnrollmentStart,
  SsoProviderConfigInput,
  SsoProviderId,
  TenantSsoView,
  TotpEnrollment,
  TrustedDeviceDto,
  Audience,
  AutomationOverview,
  AutomationRuleDto,
  AutomationRuleInput,
  AutomationRunDto,
  CodeRequestResponse,
  ConsentDto,
  ContactDto,
  ContactInput,
  ContactListParams,
  ContactStats,
  CreateJobInput,
  DailyPoint,
  GeocodeProviderSummary,
  GeocodeResponse,
  GeocodeResult,
  ImportRecord,
  ImportResult,
  ImportRowsInput,
  InvitationDto,
  InvitationPreview,
  InvitePreviewAcceptInput,
  InviteResponse,
  JobAssignment,
  JobDetailResponse,
  JobDto,
  JobRole,
  KeywordActivityResponse,
  KeywordDto,
  KeywordInput,
  KeywordsResponse,
  MembersResponse,
  MeResponse,
  MessageDto,
  OrgImportResult,
  OrgImportRowsInput,
  Page,
  PhoneNumberDto,
  PreviewOptions,
  PreviewResponse,
  PublicBranding,
  RecipientDto,
  RecipientStatus,
  ReportSummary,
  RulePreview,
  SendOptions,
  SendResponse,
  SessionResponse,
  SupportPriority,
  SupportRequestDto,
  SupportRequestInput,
  SupportRequestList,
  SupportStatus,
  TagCount,
  TenantRole,
  TenantSettingsPatch,
  TenantSettingsView,
  UpdateJobInput,
  UsageRow,
  UserDetailResponse,
  UserDto,
  UserJobDto,
  UserStatus,
  WeatherCard,
  WeatherTrigger,
  ConsentPageView,
  TenantLegalView,
  WebConsentStats,
  WebEnrollInput,
  WebEnrollResult,
  WebOptInInput,
  WebOptInResult,
} from './types';

/** API origin (empty = same origin / Vite dev proxy). */
export const BASE = ((import.meta.env.VITE_API_URL as string | undefined) ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  readonly requestId?: string;

  constructor(status: number, code: string, message: string, details?: unknown, requestId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  /** Field-level messages from a 422 (zod issues) keyed by top-level path. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    if (Array.isArray(this.details)) {
      for (const issue of this.details as Array<{ path?: unknown[]; message?: string }>) {
        const key = String(issue.path?.[0] ?? '');
        if (key && issue.message && !out[key]) out[key] = issue.message;
      }
    }
    return out;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

export function errorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (isApiError(err)) return err.message;
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}

// ---- token store (in memory; refresh token lives in an httpOnly cookie) ----

let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export const tokenStore = {
  get: () => accessToken,
  set: (t: string | null) => {
    accessToken = t;
  },
  onUnauthorized: (fn: (() => void) | null) => {
    onUnauthorized = fn;
  },
};

// ---- low-level request ----

type Query = Record<string, string | number | boolean | undefined | null>;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  formData?: FormData;
  query?: Query;
  /** Skip the automatic refresh-and-retry on 401. */
  noRefresh?: boolean;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: Query): string {
  const url = `${BASE}${path}`;
  if (!query) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${url}?${s}` : url;
}

function baseHeaders(): Record<string, string> {
  const h: Record<string, string> = { 'X-Tenant': resolveTenantSlug(), Accept: 'application/json' };
  if (accessToken) h['Authorization'] = `Bearer ${accessToken}`;
  return h;
}

let refreshInFlight: Promise<boolean> | null = null;

/** Single-flight silent refresh using the httpOnly refresh cookie. */
export async function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(buildUrl('/api/v1/auth/refresh'), {
          method: 'POST',
          credentials: 'include',
          headers: { 'X-Tenant': resolveTenantSlug(), Accept: 'application/json' },
        });
        if (!res.ok) return false;
        const data = (await res.json()) as SessionResponse;
        // The tenant chosen in this browser (?tenant= or stored) wins over a cookie session for another
        // tenant, e.g. opening /login?tenant=siemens while signed in to demo in another tab.
        if (hasStoredTenantSlug() && data.tenant?.slug && data.tenant.slug !== resolveTenantSlug()) {
          void fetch(buildUrl('/api/v1/auth/logout'), { method: 'POST', credentials: 'include' }).catch(() => undefined);
          return false;
        }
        accessToken = data.accessToken;
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      }
    })();
  }
  return refreshInFlight;
}

interface ErrorPayload {
  error?: { code?: string; message?: string; details?: unknown };
  requestId?: string;
}

async function parseError(res: Response): Promise<ApiError> {
  let payload: ErrorPayload | null = null;
  try {
    payload = (await res.json()) as ErrorPayload;
  } catch {
    /* non-JSON */
  }
  return new ApiError(
    res.status,
    payload?.error?.code ?? `http_${res.status}`,
    payload?.error?.message ?? res.statusText ?? 'Request failed',
    payload?.error?.details,
    payload?.requestId,
  );
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const doFetch = () => {
    const headers = baseHeaders();
    let body: BodyInit | undefined;
    if (opts.formData) body = opts.formData;
    else if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
    return fetch(buildUrl(path, opts.query), {
      method: opts.method ?? 'GET',
      headers,
      body,
      credentials: 'include',
      signal: opts.signal,
    });
  };

  let res = await doFetch();
  if (res.status === 401 && !opts.noRefresh && !path.startsWith('/api/v1/auth/login') && !path.startsWith('/api/v1/auth/refresh')) {
    const ok = await refreshSession();
    if (ok) res = await doFetch();
    if (res.status === 401) {
      accessToken = null;
      onUnauthorized?.();
    }
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) return (await res.json()) as T;
  return (await res.text()) as unknown as T;
}

/** Fetch a file (CSV export) with auth and trigger a browser download. */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  let res = await fetch(buildUrl(path), { headers: baseHeaders(), credentials: 'include' });
  if (res.status === 401 && (await refreshSession())) res = await fetch(buildUrl(path), { headers: baseHeaders(), credentials: 'include' });
  if (!res.ok) throw await parseError(res);
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const match = /filename="?([^";]+)"?/i.exec(cd);
  const name = match?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Top-level navigation that starts a single sign-on round-trip for the current tenant. */
export function ssoStartUrl(provider: SsoProviderId, returnTo?: string | null): string {
  const u = new URL(`${BASE}/api/v1/auth/sso/${provider}/start`, window.location.origin);
  u.searchParams.set('tenant', resolveTenantSlug());
  if (returnTo && returnTo.startsWith('/')) u.searchParams.set('return', returnTo);
  return u.toString();
}

export function isMfaPending(outcome: LoginOutcome): outcome is MfaPendingResponse {
  return 'mfa' in outcome && !!(outcome as MfaPendingResponse).mfa?.required;
}

// ---- typed endpoints ----

const j = <T>(path: string, body: unknown, method: 'POST' | 'PATCH' | 'PUT' = 'POST') => request<T>(path, { method, body });

export const api = {
  tenant: {
    // The tenant goes in the URL as well as the X-Tenant header so the browser's HTTP cache (the API allows
    // 60s of public caching) keeps one entry per tenant instead of serving another tenant's branding.
    branding: () => request<PublicBranding>('/api/v1/tenant/branding', { noRefresh: true, query: { tenant: resolveTenantSlug() } }),
    /** Public: legal documents (platform defaults or the console's wording for this organization). */
    legal: () => request<TenantLegalView>('/api/v1/tenant/legal', { noRefresh: true, query: { tenant: resolveTenantSlug() } }),
    /** Public: what the text-message sign-up page shows. */
    consentPage: () => request<ConsentPageView>('/api/v1/tenant/consent', { noRefresh: true, query: { tenant: resolveTenantSlug() } }),
    enroll: (body: WebEnrollInput) => request<WebEnrollResult>('/api/v1/tenant/consent/enroll', { method: 'POST', body, noRefresh: true, query: { tenant: resolveTenantSlug() } }),
    optIn: (body: WebOptInInput) => request<WebOptInResult>('/api/v1/tenant/consent/opt-in', { method: 'POST', body: { ...body, agreed: true }, noRefresh: true, query: { tenant: resolveTenantSlug() } }),
    optOut: (body: { phone: string }) => request<{ ok: true; phoneDisplay: string }>('/api/v1/tenant/consent/opt-out', { method: 'POST', body, noRefresh: true, query: { tenant: resolveTenantSlug() } }),
    consentStats: () => request<WebConsentStats>('/api/v1/tenant/consent/stats'),
    settings: () => request<TenantSettingsView>('/api/v1/tenant/settings'),
    updateSettings: (body: TenantSettingsPatch) => j<TenantSettingsView>('/api/v1/tenant/settings', body, 'PATCH'),
    /** Org admin: single sign-on providers and policy. */
    sso: () => request<TenantSsoView>('/api/v1/tenant/sso'),
    updateSso: (provider: SsoProviderId, body: SsoProviderConfigInput) => j<TenantSsoView>(`/api/v1/tenant/sso/${provider}`, body, 'PUT'),
    /** Org admin: devices currently signed in across the organization. */
    sessionsSummary: () => request<{ activeSessions: number }>('/api/v1/tenant/sessions/summary'),
    /** Org admin: sign every device out (the caller stays signed in). */
    revokeAllSessions: () => j<{ revoked: number }>('/api/v1/tenant/sessions/revoke-all', {}),
  },
  orgKeywords: {
    list: () => request<{ items: KeywordDto[] }>('/api/v1/org/keywords'),
    create: (body: KeywordInput) => j<{ keyword: KeywordDto }>('/api/v1/org/keywords', body),
    update: (id: string, body: Partial<KeywordInput>) => j<{ keyword: KeywordDto }>(`/api/v1/org/keywords/${id}`, body, 'PATCH'),
    remove: (id: string) => request<void>(`/api/v1/org/keywords/${id}`, { method: 'DELETE' }),
  },
  keywords: {
    list: (jobId: string) => request<KeywordsResponse>(`/api/v1/jobs/${jobId}/keywords`),
    create: (jobId: string, body: KeywordInput) => j<{ keyword: KeywordDto }>(`/api/v1/jobs/${jobId}/keywords`, body),
    update: (jobId: string, id: string, body: Partial<KeywordInput>) => j<{ keyword: KeywordDto }>(`/api/v1/jobs/${jobId}/keywords/${id}`, body, 'PATCH'),
    remove: (jobId: string, id: string) => request<void>(`/api/v1/jobs/${jobId}/keywords/${id}`, { method: 'DELETE' }),
    test: (jobId: string, body: string, language?: string) => j<{ matched: KeywordDto | null; reply: string | null; language: string }>(`/api/v1/jobs/${jobId}/keywords/test`, { body, language }),
    activity: (jobId: string, params: { keywordId?: string; page?: number; pageSize?: number } = {}) =>
      request<KeywordActivityResponse>(`/api/v1/jobs/${jobId}/keywords/activity`, { query: params }),
    qrSvg: async (jobId: string, id: string, size = 512): Promise<string> => request<string>(`/api/v1/jobs/${jobId}/keywords/${id}/qr.svg`, { query: { size } }),
  },
  automation: {
    overview: (jobId: string) => request<AutomationOverview>(`/api/v1/jobs/${jobId}/automation`),
    weather: (jobId: string, refresh = false) => request<{ forecast: WeatherCard | null; reason?: string }>(`/api/v1/jobs/${jobId}/automation/weather`, { query: { refresh: refresh ? '1' : undefined } }),
    geocode: (jobId: string, address: string, limit = 5) => j<GeocodeResponse>(`/api/v1/jobs/${jobId}/automation/geocode`, { address, limit }),
    /** The address at a pasted coordinate; result is null when the service cannot name it. */
    reverseGeocode: (jobId: string, latitude: number, longitude: number) => request<{ result: GeocodeResult | null; provider: GeocodeProviderSummary }>(`/api/v1/jobs/${jobId}/automation/geocode/reverse`, { query: { latitude, longitude } }),
    preview: (jobId: string, template: string, lookaheadHours: number, trigger?: WeatherTrigger, signal?: AbortSignal) =>
      request<RulePreview>(`/api/v1/jobs/${jobId}/automation/preview`, { method: 'POST', body: { template, lookaheadHours, trigger }, signal }),
    createRule: (jobId: string, body: AutomationRuleInput) => j<{ rule: AutomationRuleDto }>(`/api/v1/jobs/${jobId}/automation/rules`, body),
    updateRule: (jobId: string, id: string, body: Partial<AutomationRuleInput>) => j<{ rule: AutomationRuleDto }>(`/api/v1/jobs/${jobId}/automation/rules/${id}`, body, 'PATCH'),
    deleteRule: (jobId: string, id: string) => request<void>(`/api/v1/jobs/${jobId}/automation/rules/${id}`, { method: 'DELETE' }),
    evaluate: (jobId: string, id: string, force = false) =>
      request<{ evaluation: unknown; fired: { outcome: string; reason: string | null; messageId: string | null } | null; skipped: string | null }>(`/api/v1/jobs/${jobId}/automation/rules/${id}/evaluate`, {
        method: 'POST',
        query: { force: force ? '1' : undefined },
      }),
    runs: (jobId: string, params: { ruleId?: string; limit?: number } = {}) => request<{ items: AutomationRunDto[] }>(`/api/v1/jobs/${jobId}/automation/runs`, { query: params }),
  },
  auth: {
    login: (email: string, password: string) => request<LoginOutcome>('/api/v1/auth/login', { method: 'POST', body: { email, password }, noRefresh: true }),
    /** Second step of a sign-in (ticket-authorized). */
    mfa: {
      pending: () => request<MfaPendingResponse>('/api/v1/auth/mfa/pending', { method: 'POST', noRefresh: true }),
      challenge: (ticket: string, methodId: string) => request<MfaChallengeSent>('/api/v1/auth/mfa/challenge', { method: 'POST', body: { ticket, methodId }, noRefresh: true }),
      verify: (ticket: string, body: { methodId?: string | null; code: string; rememberDevice?: boolean }) =>
        request<SessionResponse & { recoveryCodesRemaining?: number }>('/api/v1/auth/mfa/verify', { method: 'POST', body: { ticket, ...body }, noRefresh: true }),
      enrollTotpStart: (ticket: string) => request<TotpEnrollment>('/api/v1/auth/mfa/enroll/totp/start', { method: 'POST', body: { ticket }, noRefresh: true }),
      enrollTotpConfirm: (ticket: string, methodId: string, code: string, rememberDevice?: boolean) =>
        request<SessionResponse & EnrollmentResult>('/api/v1/auth/mfa/enroll/totp/confirm', { method: 'POST', body: { ticket, methodId, code, rememberDevice }, noRefresh: true }),
      enrollOtpStart: (ticket: string, type: 'sms' | 'email', destination: string) =>
        request<OtpEnrollmentStart>('/api/v1/auth/mfa/enroll/otp/start', { method: 'POST', body: { ticket, type, destination }, noRefresh: true }),
      enrollOtpConfirm: (ticket: string, methodId: string, code: string, rememberDevice?: boolean) =>
        request<SessionResponse & EnrollmentResult>('/api/v1/auth/mfa/enroll/otp/confirm', { method: 'POST', body: { ticket, methodId, code, rememberDevice }, noRefresh: true }),
    },
    /** Managing your own second factors while signed in. */
    twoStep: {
      status: () => request<MfaStatus>('/api/v1/auth/me/mfa'),
      totpStart: (label?: string | null) => j<TotpEnrollment>('/api/v1/auth/me/mfa/totp/start', { label: label ?? null }),
      totpConfirm: (methodId: string, code: string) => j<EnrollmentResult>('/api/v1/auth/me/mfa/totp/confirm', { methodId, code }),
      otpStart: (type: Exclude<MfaMethodType, 'totp'>, destination: string, label?: string | null) => j<OtpEnrollmentStart>('/api/v1/auth/me/mfa/otp/start', { type, destination, label: label ?? null }),
      otpResend: (methodId: string) => j<MfaChallengeSent>('/api/v1/auth/me/mfa/otp/resend', { methodId }),
      otpConfirm: (methodId: string, code: string) => j<EnrollmentResult>('/api/v1/auth/me/mfa/otp/confirm', { methodId, code }),
      relabel: (id: string, label: string | null) => j<MfaStatus>(`/api/v1/auth/me/mfa/methods/${id}`, { label }, 'PATCH'),
      remove: (id: string) => request<MfaStatus>(`/api/v1/auth/me/mfa/methods/${id}`, { method: 'DELETE' }),
      regenerateRecoveryCodes: () => j<{ recoveryCodes: string[] }>('/api/v1/auth/me/mfa/recovery-codes', {}),
      devices: () => request<{ items: TrustedDeviceDto[] }>('/api/v1/auth/me/mfa/devices'),
      forgetDevices: () => request<{ revoked: number }>('/api/v1/auth/me/mfa/devices', { method: 'DELETE' }),
      forgetDevice: (id: string) => request<{ revoked: number }>(`/api/v1/auth/me/mfa/devices/${id}`, { method: 'DELETE' }),
    },
    logout: () => request<void>('/api/v1/auth/logout', { method: 'POST', noRefresh: true }),
    me: () => request<MeResponse>('/api/v1/auth/me'),
    updateProfile: (body: { firstName?: string; lastName?: string; phone?: string | null }) => j<{ user: UserDto }>('/api/v1/auth/me', body, 'PATCH'),
    /** Passwordless: send a one-time code to an email address or mobile number (when the tenant allows it). */
    requestCode: (identifier: string) => request<CodeRequestResponse>('/api/v1/auth/code/request', { method: 'POST', body: { identifier }, noRefresh: true }),
    verifyCode: (identifier: string, code: string) => request<LoginOutcome>('/api/v1/auth/code/verify', { method: 'POST', body: { identifier, code }, noRefresh: true }),
    changePassword: (currentPassword: string, newPassword: string) => j<void>('/api/v1/auth/change-password', { currentPassword, newPassword }),
    forgotPassword: (email: string) => request<{ ok: boolean; message: string }>('/api/v1/auth/forgot-password', { method: 'POST', body: { email }, noRefresh: true }),
    resetPassword: (token: string, password: string) => request<SessionResponse>('/api/v1/auth/reset-password', { method: 'POST', body: { token, password }, noRefresh: true }),
  },
  invitations: {
    preview: (token: string) => request<InvitationPreview>(`/api/v1/invitations/${encodeURIComponent(token)}`, { noRefresh: true }),
    accept: (token: string, body: InvitePreviewAcceptInput) =>
      request<SessionResponse>(`/api/v1/invitations/${encodeURIComponent(token)}/accept`, { method: 'POST', body, noRefresh: true }),
    list: () => request<{ items: InvitationDto[] }>('/api/v1/invitations'),
    create: (body: { email: string; tenantRole: TenantRole; jobs?: JobAssignment[]; message?: string }) => j<InviteResponse>('/api/v1/invitations', body),
    revoke: (id: string) => request<void>(`/api/v1/invitations/${id}`, { method: 'DELETE' }),
  },
  support: {
    /** Public: file a help request (works signed out; links the user when a token is present). */
    create: (body: SupportRequestInput) => request<{ request: SupportRequestDto }>('/api/v1/support/requests', { method: 'POST', body, noRefresh: true }),
    list: (params: { status?: SupportStatus; page?: number; pageSize?: number } = {}) =>
      request<SupportRequestList>('/api/v1/support/requests', { query: { status: params.status, page: params.page, pageSize: params.pageSize } }),
    get: (id: string) => request<{ request: SupportRequestDto }>(`/api/v1/support/requests/${id}`),
    update: (id: string, body: { status?: SupportStatus; priority?: SupportPriority; internalNotes?: string | null }) =>
      j<{ request: SupportRequestDto }>(`/api/v1/support/requests/${id}`, body, 'PATCH'),
    /** Conversation on a request (org admins, or the signed-in requester). */
    messages: (id: string) => request<{ items: SupportMessageDto[] }>(`/api/v1/support/requests/${id}/messages`),
    /** Reply with optional image attachments (multipart: body, internal, attachments[]). */
    reply: (id: string, input: { body: string; internal?: boolean; files?: File[] }) => {
      const fd = new FormData();
      fd.append('body', input.body);
      fd.append('internal', input.internal ? 'true' : 'false');
      for (const f of input.files ?? []) fd.append('attachments', f, f.name);
      return request<{ message: SupportMessageDto; request: SupportRequestDto }>(`/api/v1/support/requests/${id}/messages`, { method: 'POST', formData: fd });
    },
    /** The signed-in person's own requests. */
    mine: () => request<{ items: SupportRequestDto[] }>('/api/v1/support/mine'),
  },
  templates: {
    listForJob: (jobId: string) => request<{ items: MessageTemplateDto[] }>(`/api/v1/jobs/${jobId}/templates`),
    create: (jobId: string, body: MessageTemplateInput) => j<{ template: MessageTemplateDto }>(`/api/v1/jobs/${jobId}/templates`, body),
    update: (jobId: string, id: string, body: Partial<MessageTemplateInput>) => j<{ template: MessageTemplateDto }>(`/api/v1/jobs/${jobId}/templates/${id}`, body, 'PATCH'),
    remove: (jobId: string, id: string) => request<void>(`/api/v1/jobs/${jobId}/templates/${id}`, { method: 'DELETE' }),
    use: (jobId: string, id: string) => request<{ template: MessageTemplateDto }>(`/api/v1/jobs/${jobId}/templates/${id}/use`, { method: 'POST' }),
    org: {
      list: () => request<{ items: MessageTemplateDto[] }>('/api/v1/templates'),
      create: (body: MessageTemplateInput) => j<{ template: MessageTemplateDto }>('/api/v1/templates', body),
      update: (id: string, body: Partial<MessageTemplateInput>) => j<{ template: MessageTemplateDto }>(`/api/v1/templates/${id}`, body, 'PATCH'),
      remove: (id: string) => request<void>(`/api/v1/templates/${id}`, { method: 'DELETE' }),
    },
  },
  users: {
    list: () => request<{ items: UserDto[] }>('/api/v1/users'),
    get: (id: string) => request<UserDetailResponse>(`/api/v1/users/${id}`),
    update: (id: string, body: { tenantRole?: TenantRole; status?: UserStatus; firstName?: string; lastName?: string }) =>
      j<{ user: UserDto }>(`/api/v1/users/${id}`, body, 'PATCH'),
    /** Replace job assignments (PM Admin / PM User per job); unlisted jobs are removed. */
    setJobs: (id: string, jobs: JobAssignment[]) => j<{ jobs: UserJobDto[]; level: AccessLevel }>(`/api/v1/users/${id}/jobs`, { jobs }, 'PUT'),
    /** Spreadsheet import (org admin): already-parsed rows; dry runs return a verdict per row. */
    importRows: (body: OrgImportRowsInput, signal?: AbortSignal) => request<OrgImportResult>('/api/v1/users/import/rows', { method: 'POST', body, signal }),
    exportCsv: () => downloadFile('/api/v1/users/export.csv', 'users.csv'),
    exportXlsx: () => downloadFile('/api/v1/users/export.xlsx', 'users.xlsx'),
    template: () => downloadFile('/api/v1/users/import-template.csv', 'crewline-users-template.csv'),
    templateXlsx: () => downloadFile('/api/v1/users/import-template.xlsx', 'crewline-users-template.xlsx'),
  },
  jobs: {
    list: (includeArchived = false) => request<{ items: JobDto[] }>('/api/v1/jobs', { query: { includeArchived: includeArchived ? 'true' : undefined } }),
    create: (body: CreateJobInput) => j<{ job: JobDto }>('/api/v1/jobs', body),
    availableNumbers: () => request<{ items: PhoneNumberDto[] }>('/api/v1/jobs/phone-numbers/available'),
    get: (jobId: string) => request<JobDetailResponse>(`/api/v1/jobs/${jobId}`),
    update: (jobId: string, body: UpdateJobInput) => j<{ job: JobDto }>(`/api/v1/jobs/${jobId}`, body, 'PATCH'),
    /** Cover photo (PNG, JPEG, GIF or WebP, up to 5 MB) in the multipart field "file". */
    uploadCover: (jobId: string, file: File) => {
      const fd = new FormData();
      fd.append('file', file, file.name);
      return request<{ job: JobDto }>(`/api/v1/jobs/${jobId}/cover`, { method: 'POST', formData: fd });
    },
    removeCover: (jobId: string) => request<{ job: JobDto }>(`/api/v1/jobs/${jobId}/cover`, { method: 'DELETE' }),
    members: (jobId: string) => request<MembersResponse>(`/api/v1/jobs/${jobId}/members`),
    addMember: (jobId: string, userId: string, role: JobRole) => j<{ member: UserDto & { role: JobRole } }>(`/api/v1/jobs/${jobId}/members`, { userId, role }),
    inviteMember: (jobId: string, body: { email: string; role: JobRole; message?: string }) => j<InviteResponse>(`/api/v1/jobs/${jobId}/members/invite`, body),
    updateMember: (jobId: string, userId: string, role: JobRole) => j<{ role: JobRole }>(`/api/v1/jobs/${jobId}/members/${userId}`, { role }, 'PATCH'),
    removeMember: (jobId: string, userId: string) => request<void>(`/api/v1/jobs/${jobId}/members/${userId}`, { method: 'DELETE' }),
    revokeInvitation: (jobId: string, id: string) => request<void>(`/api/v1/jobs/${jobId}/invitations/${id}`, { method: 'DELETE' }),
    /** Spreadsheet import (org admin): already-parsed rows; dry runs return a verdict per row. */
    importRows: (body: OrgImportRowsInput, signal?: AbortSignal) => request<OrgImportResult>('/api/v1/jobs/import/rows', { method: 'POST', body, signal }),
    exportCsv: () => downloadFile('/api/v1/jobs/export.csv', 'jobs.csv'),
    exportXlsx: () => downloadFile('/api/v1/jobs/export.xlsx', 'jobs.xlsx'),
    template: () => downloadFile('/api/v1/jobs/import-template.csv', 'crewline-jobs-template.csv'),
    templateXlsx: () => downloadFile('/api/v1/jobs/import-template.xlsx', 'crewline-jobs-template.xlsx'),
  },
  contacts: {
    list: (jobId: string, params: ContactListParams) => request<Page<ContactDto>>(`/api/v1/jobs/${jobId}/contacts`, { query: params as Query }),
    tags: (jobId: string) => request<{ items: TagCount[] }>(`/api/v1/jobs/${jobId}/contacts/tags`),
    stats: (jobId: string) => request<ContactStats>(`/api/v1/jobs/${jobId}/contacts/stats`),
    imports: (jobId: string) => request<{ items: ImportRecord[] }>(`/api/v1/jobs/${jobId}/contacts/imports`),
    get: (jobId: string, contactId: string) => request<{ contact: ContactDto }>(`/api/v1/jobs/${jobId}/contacts/${contactId}`),
    create: (jobId: string, body: ContactInput) => j<{ contact: ContactDto }>(`/api/v1/jobs/${jobId}/contacts`, body),
    update: (jobId: string, contactId: string, body: ContactInput & { optedOut?: boolean }) =>
      j<{ contact: ContactDto }>(`/api/v1/jobs/${jobId}/contacts/${contactId}`, body, 'PATCH'),
    remove: (jobId: string, contactId: string) => request<void>(`/api/v1/jobs/${jobId}/contacts/${contactId}`, { method: 'DELETE' }),
    bulkDelete: (jobId: string, contactIds: string[]) => j<{ deleted: number }>(`/api/v1/jobs/${jobId}/contacts/bulk-delete`, { contactIds }),
    bulkTags: (jobId: string, contactIds: string[], add: string[], remove: string[]) =>
      j<{ updated: number }>(`/api/v1/jobs/${jobId}/contacts/bulk-tags`, { contactIds, add, remove }),
    import: (jobId: string, formData: FormData) => request<ImportResult>(`/api/v1/jobs/${jobId}/contacts/import`, { method: 'POST', formData }),
    importRows: (jobId: string, body: ImportRowsInput, signal?: AbortSignal) => request<ImportResult>(`/api/v1/jobs/${jobId}/contacts/import/rows`, { method: 'POST', body, signal }),
    consents: (jobId: string, contactId: string) => request<{ items: ConsentDto[] }>(`/api/v1/jobs/${jobId}/contacts/${contactId}/consents`),
    exportCsv: (jobId: string) => downloadFile(`/api/v1/jobs/${jobId}/contacts/export.csv`, 'contacts.csv'),
    template: (jobId: string) => downloadFile(`/api/v1/jobs/${jobId}/contacts/import-template.csv`, 'crewline-contacts-template.csv'),
    templateXlsx: (jobId: string) => downloadFile(`/api/v1/jobs/${jobId}/contacts/import-template.xlsx`, 'crewline-contacts-template.xlsx'),
  },
  messages: {
    list: (jobId: string, params: { page?: number; pageSize?: number; status?: string; view?: 'upcoming' | 'sent' }) =>
      request<Page<MessageDto>>(`/api/v1/jobs/${jobId}/messages`, { query: params }),
    preview: (jobId: string, body: string, audience?: Audience, options: PreviewOptions = {}, signal?: AbortSignal) =>
      request<PreviewResponse>(`/api/v1/jobs/${jobId}/messages/preview`, { method: 'POST', body: { body, audience, ...options }, signal }),
    send: (jobId: string, body: string, audience: Audience, options: SendOptions = {}) => j<SendResponse>(`/api/v1/jobs/${jobId}/messages`, { body, audience, ...options }),
    get: (jobId: string, messageId: string) => request<{ message: MessageDto }>(`/api/v1/jobs/${jobId}/messages/${messageId}`),
    update: (jobId: string, messageId: string, body: { body?: string; audience?: Audience; translate?: boolean; translationOverrides?: Record<string, string>; scheduledAt?: string | null }) =>
      j<{ message: MessageDto }>(`/api/v1/jobs/${jobId}/messages/${messageId}`, body, 'PATCH'),
    sendNow: (jobId: string, messageId: string, bypassQuietHours = false) => j<SendResponse>(`/api/v1/jobs/${jobId}/messages/${messageId}/send`, { bypassQuietHours }),
    cancel: (jobId: string, messageId: string) => j<{ message: MessageDto }>(`/api/v1/jobs/${jobId}/messages/${messageId}/cancel`, {}),
    recipients: (jobId: string, messageId: string, params: { page?: number; pageSize?: number; status?: RecipientStatus }) =>
      request<Page<RecipientDto>>(`/api/v1/jobs/${jobId}/messages/${messageId}/recipients`, { query: params }),
    /** One recipient's receipt; `renderedBody` is always the exact text that person was sent. */
    recipient: (jobId: string, messageId: string, recipientId: string) => request<{ recipient: RecipientDto; message: MessageDto }>(`/api/v1/jobs/${jobId}/messages/${messageId}/recipients/${recipientId}`),
    exportCsv: (jobId: string, messageId: string) => downloadFile(`/api/v1/jobs/${jobId}/messages/${messageId}/export.csv`, 'recipients.csv'),
  },
  reports: {
    summary: (jobId: string, range?: { from?: string; to?: string }) =>
      request<{ summary: ReportSummary }>(`/api/v1/jobs/${jobId}/reports/summary`, { query: range }),
    daily: (jobId: string, range?: { from?: string; to?: string }) =>
      request<{ range: { from: string; to: string }; series: DailyPoint[] }>(`/api/v1/jobs/${jobId}/reports/daily`, { query: range }),
    usage: () => request<{ items: UsageRow[] }>('/api/v1/reports/usage'),
  },
};
